import {
	getListProjectNetworksQueryOptions,
	type Network,
	type Project,
	useListProjectNetworksSuspense,
} from '@amarantos/api-client'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { type NetworkTypeFilterValue, networksSearchSchema, ProjectNetworksList } from '@/features/networks'
import { NetworkFormDialog } from '@/features/planning/components/network-form-dialog'
import { useDeleteNetworkMutation } from '@/features/planning/hooks/use-network-mutations'
import { toListProjectNetworksParams } from '@/features/planning/utils/search'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { useRetainedValue } from '@/hooks/use-retained-value'
import { useWholeListTruncationWarning } from '@/hooks/use-whole-list-truncation-warning'
import { getApiErrorMessage } from '@/utils/errors'

// The project's single Sieci tab: every network as a table row carrying its two entry points —
// Edycja opens the fullscreen editor's authoring canvas, Podgląd its read-only status lens
// (see `NetworkActions`). Networks are created, renamed and deleted from here; the old
// Planowanie / Podgląd tabs collapsed into this list.
export const Route = createFileRoute('/_authenticated/projects/$projectId/networks')({
	validateSearch: networksSearchSchema,
	loader: ({ context: { queryClient }, params: { projectId } }) => {
		queryClient.ensureQueryData(getListProjectNetworksQueryOptions(projectId, toListProjectNetworksParams()))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_networks' />
	),
	component: ProjectNetworksRoute,
})

function ProjectNetworksRoute() {
	const { t } = useTranslation()
	const { projectId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()

	const networksQuery = useListProjectNetworksSuspense(projectId, toListProjectNetworksParams())
	useWholeListTruncationWarning('networks', networksQuery.data)
	const networks = networksQuery.data.data
	const filteredNetworks = search.type ? networks.filter((network) => network.type === search.type) : networks

	// The network the edit (rename / type) dialog targets — set when a row's menu opens it.
	const selectedNetwork = networks.find((network) => network.id === search.networkId) ?? null
	const dialogNetwork = useRetainedValue(selectedNetwork ?? undefined)

	const deleteNetwork = useConfirmAction<Network, { projectId: Project['id']; id: Network['id'] }>({
		mutation: useDeleteNetworkMutation(projectId, (networkId) => {
			// Deleting the network the edit dialog points at drops that pointer so it can't reopen on a ghost.
			if (networkId === search.networkId) {
				navigate({ search: (previous) => ({ ...previous, networkId: undefined }) })
			}
		}),
		toVariables: (network) => ({ projectId, id: network.id }),
		formatError: (error) => getApiErrorMessage(error, t('error.delete_network')),
	})

	const closeDialog = (open: boolean) => {
		if (!open) {
			navigate({ search: (previous) => ({ ...previous, dialog: undefined, networkId: undefined }) })
		}
	}

	const setTypeFilter = (value: NetworkTypeFilterValue) => {
		navigate({ search: (previous) => ({ ...previous, type: value === 'all' ? undefined : value }) })
	}

	return (
		<>
			<ProjectNetworksList
				projectId={projectId}
				networks={filteredNetworks}
				totalCount={networks.length}
				typeFilter={search.type ?? 'all'}
				onTypeFilterChange={setTypeFilter}
				onCreate={() => navigate({ search: (previous) => ({ ...previous, dialog: 'create' as const }) })}
				onEdit={(network) =>
					navigate({ search: (previous) => ({ ...previous, networkId: network.id, dialog: 'edit' as const }) })
				}
				onDelete={(network) => deleteNetwork.request(network)}
			/>

			<NetworkFormDialog
				open={search.dialog === 'create'}
				mode='create'
				projectId={projectId}
				onOpenChange={closeDialog}
				onSuccess={(network) => {
					// A new network opens straight into the fullscreen editor to be drawn.
					navigate({
						to: '/projects/$projectId/planning-workspace',
						params: { projectId },
						search: { networkId: network.id },
					})
				}}
			/>

			{!!dialogNetwork && (
				<NetworkFormDialog
					open={search.dialog === 'edit'}
					mode='edit'
					projectId={projectId}
					network={dialogNetwork}
					onOpenChange={closeDialog}
					onSuccess={() => {
						navigate({ search: (previous) => ({ ...previous, dialog: undefined }) })
					}}
				/>
			)}

			<ConfirmDeleteDialog
				{...deleteNetwork.dialogProps}
				title={t('planning.delete.title')}
				description={t('planning.delete.description', {
					name: deleteNetwork.target?.name ?? t('planning.delete.fallback_name'),
				})}
			/>
		</>
	)
}
